import { Badge, Card, Metric } from "@/design-system";
import { t } from "@/context/language";
import {
  AnimatePresence,
  animate,
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from "framer-motion";
import { useEffect, useRef, useState } from "react";
import {
  HERO_PAYMENTS,
  HERO_PAYMENTS_START_TOTAL,
  formatBRL,
} from "../constants/heroPayments";
import { HeroPaymentRow } from "./heroPaymentRow";

const VISIBLE_ROWS = 3;
const TICK_MS = 2800;
// A linha mais antiga apaga aos poucos, como quem desce no extrato.
const ROW_OPACITY = [1, 0.8, 0.5];

const paymentAt = (index: number) =>
  HERO_PAYMENTS[index % HERO_PAYMENTS.length];

export function HeroPaymentsCard() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { margin: "-80px" });
  const reduceMotion = useReducedMotion();

  // `tick` conta quantos pagamentos já chegaram; a linha do topo é a mais nova.
  const [tick, setTick] = useState(0);
  const total = useMotionValue(HERO_PAYMENTS_START_TOTAL);
  const totalLabel = useTransform(total, formatBRL);

  useEffect(() => {
    if (!inView || reduceMotion) return;
    const id = window.setInterval(() => setTick(prev => prev + 1), TICK_MS);
    return () => window.clearInterval(id);
  }, [inView, reduceMotion]);

  useEffect(() => {
    if (tick === 0) return;
    const incoming = paymentAt(tick + VISIBLE_ROWS - 1);
    const controls = animate(total, total.get() + incoming.amount, {
      duration: 0.9,
      ease: [0.16, 1, 0.3, 1],
    });
    return () => controls.stop();
  }, [tick, total]);

  const rows = Array.from({ length: VISIBLE_ROWS }, (_, position) => {
    const index = tick + VISIBLE_ROWS - 1 - position;
    return { index, payment: paymentAt(index) };
  });

  return (
    // `isolate`: sem um contexto de empilhamento próprio, o halo com `-z-10`
    // desce para trás do fundo do hero e some.
    <figure ref={ref} className="relative isolate w-full max-w-md lg:max-w-lg">
      <figcaption className="sr-only">{t.hero.paymentsCard.caption}</figcaption>

      {/* Halo laranja: uma névoa larga em volta do card e um núcleo mais
          denso embaixo, que dá a sensação de luz vindo de trás. */}
      <div
        aria-hidden="true"
        className="bg-ds-brand-100 absolute -inset-x-6 -inset-y-10 -z-10 rounded-full opacity-80 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="bg-ds-brand-200 absolute inset-x-10 -bottom-8 -z-10 h-1/3 rounded-full opacity-70 blur-3xl"
      />

      <Card
        elevation="raised"
        className="rounded-ds-xl bg-ds-paper-marketing flex flex-col gap-6 sm:p-8"
        aria-hidden="true"
      >
        {/* O selo fica fora do fluxo: dividindo a linha com o total, ele
            empurrava os centavos para uma segunda linha no desktop. */}
        <div className="relative">
          <Metric
            label={t.hero.paymentsCard.label}
            hint={t.hero.paymentsCard.hint}
            value={
              <motion.span className="sm:text-ds-h1 lg:text-ds-display whitespace-nowrap">
                {totalLabel}
              </motion.span>
            }
          />
          <Badge tone="success" shape="pill" className="absolute top-0 right-0">
            <span className="bg-ds-success-700 size-1.5 rounded-full" />
            {t.hero.paymentsCard.badge}
          </Badge>
        </div>

        <ul className="flex flex-col gap-3">
          <AnimatePresence initial={false} mode="popLayout">
            {rows.map(({ index, payment }, position) => (
              <motion.li
                key={index}
                layout={!reduceMotion}
                initial={{ opacity: 0, y: -24, scale: 0.97 }}
                animate={{ opacity: ROW_OPACITY[position], y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <HeroPaymentRow payment={payment} />
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>
      </Card>
    </figure>
  );
}
